import { parseMinorUnits, type WireMoney } from '../money.js'

/**
 * `GET /orders/:orderId/claim` as the counter uses it. §6.13 `CLM-002`, `CLM-003`; §15.20 steps 1
 * to 3.
 *
 * A mapper rather than a cast, for the reason `CLAUDE.md` gives: three screens crashed at runtime
 * on fields the server does not send and all three typechecked. Every money field is read by name
 * and `parseMinorUnits` is what decides whether a figure is a figure, so a claim whose split did
 * not arrive is a message and not a blank row in the middle of a receivable.
 *
 * It lives here rather than in `reads.ts` so it can be tested on its own, and because the shape it
 * produces is this panel's rather than the wire's. `reads.ts` keeps the request.
 */

export interface ClaimCapturePlan {
  readonly id: string
  readonly panelId: string
  /** The payer's name, for the chooser. */
  readonly panel: string
  readonly name: string
  readonly currency: string
  /** When true the write needs an authorisation reference and the day it was granted. */
  readonly authorisationRequired: boolean
  readonly offered: boolean
  /** The server's own code and sentence for a plan it will not take. */
  readonly code?: string
  readonly message?: string
}

export interface ClaimCaptureLine {
  readonly orderLineId: string
  readonly category: string
  readonly price: WireMoney
  /** What the panel owes on this line. A receivable, never a tender. */
  readonly payer: WireMoney
  /** What the customer still owes on this line. */
  readonly customer: WireMoney
}

export interface ClaimCaptureClaim {
  readonly id: string
  readonly status: string
  readonly panelId: string
  readonly planId: string
  readonly capturedOn: string
  readonly submissionCount: number
  readonly authorisationReference?: string
  readonly claimed: WireMoney
  readonly customerShare: WireMoney
  readonly paid: WireMoney
  readonly outstanding: WireMoney
  readonly denied: WireMoney
  readonly transferred: WireMoney
  readonly writtenOff: WireMoney
  readonly reduced: WireMoney
  readonly owedToPayer: WireMoney
  readonly lines: readonly ClaimCaptureLine[]
  /** The optimistic lock every event carries (`ARC-006`'s neighbour, Appendix J). */
  readonly version: number
}

export interface ClaimCaptureData {
  readonly orderId: string
  readonly branchId: string
  readonly currency: string
  /** Today at the order's branch, which is the day a plan's window was read against. */
  readonly capturedOn: string
  /** Whether this order can carry a new claim at all. */
  readonly offered: boolean
  readonly code?: string
  readonly message?: string
  readonly plans: readonly ClaimCapturePlan[]
  readonly claim?: ClaimCaptureClaim
}

const asRecord = (value: unknown, what: string): Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`The server sent ${what} in a shape this app does not understand.`)
  }
  return value as Record<string, unknown>
}

const text = (wire: Record<string, unknown>, key: string, what: string): string => {
  const value = wire[key]
  if (typeof value !== 'string') throw new Error(`${what} arrived without ${key}.`)
  return value
}

const optionalText = (wire: Record<string, unknown>, key: string): string | undefined =>
  typeof wire[key] === 'string' ? (wire[key] as string) : undefined

const count = (wire: Record<string, unknown>, key: string, what: string): number => {
  const value = wire[key]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${what} arrived without ${key}.`)
  }
  return value
}

/** One money column, currency included, because `MR-01` says that is half of what money is. */
const money = (wire: Record<string, unknown>, key: string, what: string): WireMoney => {
  const value = asRecord(wire[key], `${what} ${key}`)
  const minor = text(value, 'minor', `${what} ${key}`)
  try {
    parseMinorUnits(minor)
  } catch {
    throw new Error(`${what} sent ${key} as something that is not a whole amount.`)
  }
  return { minor, currency: text(value, 'currency', `${what} ${key}`) }
}

const list = (value: unknown, what: string): readonly unknown[] => {
  if (!Array.isArray(value))
    throw new Error(`The server sent ${what} as something other than a list.`)
  return value
}

export function parseClaimCapture(body: unknown): ClaimCaptureData {
  const wire = asRecord(body, 'the panel share for this order')
  const what = 'the panel share'
  const claimWire = wire['claim'] === undefined ? undefined : asRecord(wire['claim'], 'the claim')

  return {
    orderId: text(wire, 'orderId', what),
    branchId: text(wire, 'branchId', what),
    currency: text(wire, 'currency', what),
    capturedOn: text(wire, 'capturedOn', what),
    offered: wire['offered'] === true,
    ...withheld(wire),
    plans: list(wire['plans'], 'the plans').map((row) => {
      const plan = asRecord(row, 'a plan')
      return {
        id: text(plan, 'id', 'a plan'),
        panelId: text(plan, 'panelId', 'a plan'),
        panel: text(plan, 'panel', 'a plan'),
        name: text(plan, 'name', 'a plan'),
        currency: text(plan, 'currency', 'a plan'),
        authorisationRequired: plan['authorisationRequired'] === true,
        offered: plan['offered'] === true,
        ...withheld(plan),
      }
    }),
    ...(claimWire === undefined ? {} : { claim: parseClaim(claimWire) }),
  }
}

/**
 * The code and the sentence, passed through as sent.
 *
 * The server writes the sentence because the server knows the reason, and it names a status, a
 * month or two totals, so it cannot be a catalogue key without a `details` object to fill one
 * (`refusals.ts` says so and `later_todo.md` 48 carries it).
 */
function withheld(wire: Record<string, unknown>): { code?: string; message?: string } {
  const code = optionalText(wire, 'code')
  const message = optionalText(wire, 'message')
  return {
    ...(code === undefined ? {} : { code }),
    ...(message === undefined ? {} : { message }),
  }
}

function parseClaim(wire: Record<string, unknown>): ClaimCaptureClaim {
  const what = 'the claim'
  const reference = optionalText(wire, 'authorisationReference')
  return {
    id: text(wire, 'id', what),
    status: text(wire, 'status', what),
    panelId: text(wire, 'panelId', what),
    planId: text(wire, 'planId', what),
    capturedOn: text(wire, 'capturedOn', what),
    submissionCount: count(wire, 'submissionCount', what),
    ...(reference === undefined ? {} : { authorisationReference: reference }),
    claimed: money(wire, 'claimed', what),
    customerShare: money(wire, 'customerShare', what),
    paid: money(wire, 'paid', what),
    outstanding: money(wire, 'outstanding', what),
    denied: money(wire, 'denied', what),
    transferred: money(wire, 'transferred', what),
    writtenOff: money(wire, 'writtenOff', what),
    reduced: money(wire, 'reduced', what),
    owedToPayer: money(wire, 'owedToPayer', what),
    lines: list(wire['lines'], 'the claim lines').map((row) => {
      const line = asRecord(row, 'a claim line')
      return {
        orderLineId: text(line, 'orderLineId', 'a claim line'),
        category: text(line, 'category', 'a claim line'),
        price: money(line, 'price', 'a claim line'),
        payer: money(line, 'payer', 'a claim line'),
        customer: money(line, 'customer', 'a claim line'),
      }
    }),
    version: count(wire, 'version', what),
  }
}
